import React, { useState, useEffect, useContext } from 'react';
import { Redirect, useHistory } from 'react-router-dom';
import axios from 'axios';
import { styled } from '@this/constants/themes';
import { observer } from 'mobx-react';
import Container from '@material-ui/core/Container';
import Paper from '@material-ui/core/Paper';
import TextField from '@material-ui/core/TextField';
import Box from '@material-ui/core/Box';
import Link from '@material-ui/core/Link';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import { H2, LoadingBackdrop } from '@this/components/appoint/common';
import User from '@this/domain/user/user';
import type UserJson from '@this/domain/user/user_json';
import AppointContext from '@this/components/appoint/appoint_context';
import Utils from '@this/domain/appoint/utils';

interface UserResponse {
  user: UserJson;
}

const UsersInvitationAccept = observer(() => {
  const contextValue = useContext(AppointContext);
  const history = useHistory();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [email, setEmail] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [lastNameError, setLastNameError] = useState<string | null>(null);
  const [firstName, setFirstName] = useState<string>('');
  const [firstNameError, setFirstNameError] = useState<string | null>(null);
  const [password, setPassword] = useState<string>('');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [succeeded, setSucceeded] = useState<boolean>(false);
  const invitationToken = Utils.getParam('invitation_token');

  const fetchUser = async () => {
    setIsLoading(true);
    const params = { invitation_token: invitationToken };
    const res = await axios.get<UserResponse>('/aicalendar/users/invitation/accept', { params });
    const user = new User(res.data.user);
    setLastName(user.lastName || '');
    setFirstName(user.firstName || '');
    setEmail(user.email);
    setIsLoading(false);
  };

  // マウント時に1回発動
  useEffect(() => {
    fetchUser();
    Utils.trackEvent('[cal][view] invitation accept');
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setLastNameError(null);
    setFirstNameError(null);
    setPasswordError(null);
    setErrorMessage(null);
    try {
      const params = {
        'user[last_name]': lastName,
        'user[first_name]': firstName,
        'user[password]': password,
        'user[invitation_token]': invitationToken
      };
      await axios.put('/aicalendar/users/invitation.json', params);
      contextValue.onUserSignedIn();
      setSucceeded(true);
    } catch (e) {
      if (e.status === 400) {
        if (e.responseJSON && e.responseJSON.errors) {
          setLastNameError(e.responseJSON.errors.last_name);
          setFirstNameError(e.responseJSON.errors.first_name);
          setPasswordError(e.responseJSON.errors.password);
        }
      } else {
        setErrorMessage('通信エラーが発生しました。時間をおいて再度お試しください。');
      }
    }
    setIsSubmitting(false);
  };

  return (
    <Container maxWidth="sm">
      <LoadingBackdrop open={isLoading} />
      {succeeded && <Redirect to={history.createHref({ pathname: '/' })} />}
      <Paper style={{ padding: '24px' }}>
        <H2>招待の承認</H2>
        <form onSubmit={e => handleSubmit(e)}>
          <TheTextField variant="outlined" label="メールアドレス" value={email} disabled />
          <Box display="flex">
            <TheTextField
              variant="outlined"
              label="姓（必須）"
              value={lastName}
              helperText={lastNameError}
              error={!!lastNameError}
              onChange={e => setLastName(e.target.value)}
              style={{ marginRight: '10px' }}
            />
            <TheTextField
              variant="outlined"
              label="名（必須）"
              value={firstName}
              helperText={firstNameError}
              error={!!firstNameError}
              onChange={e => setFirstName(e.target.value)}
            />
          </Box>
          <TheTextField
            variant="outlined"
            type="password"
            label="パスワード（必須）"
            value={password}
            helperText={passwordError}
            error={!!passwordError}
            onChange={e => setPassword(e.target.value)}
          />
          {errorMessage && (
            <Error>
              {errorMessage.split('\n').map((line, i) => (
                <div key={i.toString()}>{line}</div>
              ))}
            </Error>
          )}
          <Box marginBottom="20px" textAlign="center">
            <Link href="https://aitravel.cloud/aicalendar/tos" target="_blank">
              利用規約
            </Link>
            に同意の上、ご登録ください
          </Box>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={isSubmitting}
            style={{ display: 'block', marginLeft: 'auto', marginRight: 'auto' }}
          >
            招待を承認して利用開始
          </Button>
        </form>
      </Paper>
    </Container>
  );
});

const TheTextField = styled(TextField)`
  width: 100%;
  margin-bottom: 24px;
`;

const Error = styled(Typography).attrs(() => ({ color: 'error' }))`
  margin-bottom: 24px;
`;

export default UsersInvitationAccept;
